import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import ErrorIcon from '@material-ui/icons/Error';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import { mapTranslations, getSourceInformation } from '../libs/parseVendorData';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    backgroundColor: theme.palette.background.paper,
  },
  errorItem: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'flex-start',
    padding: theme.spacing(2),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  errorIcon: {
    color: theme.palette.error.main,
    minWidth: '40px',
    marginTop: '4px',
  },
  sourceColumn: {
    flex: '0 0 200px',
    marginRight: theme.spacing(2),
  },
  errorsColumn: {
    flex: '1',
  },
  noErrors: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  errorMessage: {
    color: theme.palette.error.main,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(0.5),
  },
  featureInfo: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  sourceLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  identValue: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
}));

export default function ErrorFeatureList({ geojson, onFeatureClick }) {
  const classes = useStyles();

  const errorFeatures = useMemo(() => {
    if (!geojson || !geojson.features) return [];
    
    return geojson.features
      .filter(feature => feature.properties && feature.properties.error_msg)
      .map(feature => {
        const sourceInfo = getSourceInformation(feature);
        const mapping = mapTranslations(feature) || {};
        const source = sourceInfo?.featuretype || 'Ukendt kilde';
        const id = sourceInfo?.ident || 'Ukendt id';
        const idCol = mapping.feature?.ident_col || 'Ukendt kolonne';
        const tests = feature.properties.error_msg.tests || [];

        // If feature.properties.error_msg is just a string, wrap it in an array
        if (typeof feature.properties.error_msg === 'string') {
          tests.push({ attribute: 'Systemfejl', message: feature.properties.error_msg });
        }

        
        return {
          feature,
          source,
          id,
          tests,
          idCol
        };
      });
  }, [geojson]);

  const handleFeatureClick = (feature) => {
    if (onFeatureClick) {
      onFeatureClick(feature);
    }
  };

  if (errorFeatures.length === 0) {
    return (
      <div className={classes.noErrors}>
        <Typography variant="body2">Ingen fejl fundet i data</Typography>
      </div>
    );
  }
  
  return (
    <List className={classes.root}>
      {errorFeatures.map((item, index) => (
        <ListItem
          key={index}
          className={classes.errorItem}
          onClick={() => handleFeatureClick(item.feature)}
          button
        >
          <ListItemIcon className={classes.errorIcon}>
            <ErrorIcon />
          </ListItemIcon>
          
          <div className={classes.sourceColumn}>
            <Typography className={classes.sourceLabel}>
              {item.source}
            </Typography>
            <Typography className={classes.identValue}>
              {item.idCol} = {item.id}
            </Typography>
          </div>
          
          <div className={classes.errorsColumn}>
            {item.tests.map((test, testIndex) => (
              <Typography 
                key={testIndex} 
                variant="body2" 
                className={classes.errorMessage}
              >
                <strong>{test.attribute}:</strong> {test.message || test}
              </Typography>
            ))}
            {item.tests.length === 0 && (
              <Typography variant="body2" className={classes.featureInfo}>
                Ingen fejlbeskeder
              </Typography>
            )}
          </div>
        </ListItem>
      ))}
    </List>
  );
}
